import "./index.scss";

import { AnalyticsProvider } from "@codat/analytics";
import {
  CodatAuthContext,
  CodatAuthContextType,
  CodatAuthProvider,
  User,
  UserManager,
} from "@codat/client-cookie-manager";
import { createRoot } from "react-dom/client";
import Cookies from "universal-cookie";
import { ErrorBoundary } from "react-error-boundary";

import App from "components/App";
import config from "config";
import auth0Config from "config/auth0Config";
import oidcConfig from "config/oidcConfig";
import { CenteredSpinner } from "components/CenteredSpinner";
import { ErrorDialog, OrchardProvider } from "@codat/orchard-ui";
import { useMemo } from "react";
import { createLogger } from "utils/logger";

function assertsIsElement(node: Element | null): asserts node is Element {
  if (!node) {
    throw new Error("node is null");
  }
}

const element = document.getElementById("root");
assertsIsElement(element);
const root = createRoot(element);

const cookies = new Cookies();
const tokenCookie = cookies.get("mock_token");

const mockOIDC: CodatAuthContextType = {
  roles: ["Developer"],
  signIn: () => {
    cookies.set("last-auth-action", "sign-in");
    return Promise.resolve();
  },
  signInPopup: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  signOutRedirect: () => Promise.resolve(),
  authType: "OIDC",
  userManager: {
    startSilentRenew: () => {
      //void
    },
    stopSilentRenew: () => {
      //void
    },
    signinSilent: () => Promise.resolve({} as User),
  } as unknown as UserManager,
  userData: {
    access_token: tokenCookie,
  } as User,
  isLoading: false,
  selectClient: () => {
    /* This function intentionally left blank */
  },
  clearAuth0Cookies: () => {
    /* This function intentionally left blank */
  },
};

const mockAuth0: CodatAuthContextType = {
  signIn: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  signOutRedirect: () => Promise.resolve(),
  authType: "Auth0",
  userManager: {
    signoutRedirect: () => {
      //void
    },
  } as unknown as UserManager,
  userData: {
    access_token: tokenCookie,
  } as User,
  isLoading: false,
  selectClient: () => {
    /* This function intentionally left blank */
  },
  clearAuth0Cookies: () => {
    //void
  },
  getAccessTokenWithPopup: () => Promise.resolve<string>("ok"),
  getIdTokenClaims: () => Promise.resolve<undefined>(undefined),
  loginWithRedirect: () => Promise.resolve(),
  loginWithPopup: () => Promise.resolve(),
  logout: () => {
    //void
  },
  handleRedirectCallback: () => Promise.resolve({ appState: "test" }),
} as unknown as CodatAuthContextType;

const AuthWrapper: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const doDebugLog = useMemo(() => {
    return cookies.get("debug-codat-auth") === true;
  }, []);

  const { log } = createLogger(doDebugLog);

  if (window.origin.match("localhost") && tokenCookie !== undefined) {
    return (
      <CodatAuthContext.Provider
        value={cookies.get("isAuth0User") === true ? mockAuth0 : mockOIDC}
      >
        {children}
      </CodatAuthContext.Provider>
    );
  } else {
    // Remove cookie identifying Auth0 user, so it doesn't affect normal login
    cookies.remove(`isAuth0User-${config.ENVIRONMENT}`, {
      path: "/",
      domain: ".codat.io",
    });

    const useAuth0Login = config.DEFAULT_TO_AUTH0 === "true";
    const isLegacyAuthCookieEnabled =
      cookies.get(`isLegacyAuthUser-${config.ENVIRONMENT}`) === true;

    const useAuth0 = useAuth0Login && !isLegacyAuthCookieEnabled;

    log(`useAuth0Login: ${useAuth0Login}`);
    log(`isLegacyAuthCookieEnabled: ${isLegacyAuthCookieEnabled}`);
    log(`useAuth0: ${useAuth0}`);

    const auth0ConfigForProvider = useAuth0 ? auth0Config : undefined;

    return (
      <CodatAuthProvider
        oidcConfig={oidcConfig}
        auth0Config={auth0ConfigForProvider}
        isAuth0UserCookieKey={`isAuth0User-${config.ENVIRONMENT}`}
        loadingFallback={<CenteredSpinner />}
        requireClientCookie={false}
      >
        {children}
      </CodatAuthProvider>
    );
  }
};

root.render(
  <OrchardProvider appId="app-user-management">
    <ErrorBoundary
      fallbackRender={({ error: { statusCode, correlationId, message } }) => (
        <ErrorDialog
          statusCode={statusCode}
          correlationId={correlationId}
          message={message}
        />
      )}
    >
      <AnalyticsProvider
        config={{
          apiKey: config.AMPLITUDE_API_KEY,
          appName: "User Management",
          mode:
            config.ENVIRONMENT === "Integration" ? "development" : "production",
        }}
      >
        <AuthWrapper>
          <App />
        </AuthWrapper>
      </AnalyticsProvider>
    </ErrorBoundary>
  </OrchardProvider>
);
